// =============================================================================
// RoleEditForm: in-place edit surface for a role's own fields (LANE
// ROLE-REFINE — parity matrix gap #6: "the ability to refine the roles… was
// missing"). Field set mirrors apps/web's EditRoleSheet.tsx exactly (company,
// title, location, employerDescriptor, startDate, endDate, isCurrent,
// summary) — the brief scopes to "title, company, dates, location" as the
// header-visible fields; employerDescriptor + summary are additionally
// exposed here because the server's PATCH already accepts them and apps/web's
// own edit surface treats them as part of "editing a role" (not extending
// scope beyond what's already grounded in the existing edit contract).
//
// Renders INLINE on the corpus canvas (RoleGroupSection's header), not as a
// modal sheet — this tab's whole surface is the canvas itself, so a role edit
// opens in place rather than overlaying a separate sheet. Text fields route
// through VoiceTextarea (the mic-icon-bearing field this app already
// established — VoiceTextarea.tsx) per the visual law ("every textarea has a
// mic icon"); dates and the isCurrent checkbox are not voice-dictatable
// fields, so they stay plain <input> controls, matching apps/web's own
// EditRoleSheet choice not to wire voice onto those two controls either.
//
// NO INVENTION: every field here is the user's own typed/dictated edit —
// nothing is generated, suggested, or auto-filled. Save only ever sends what
// the user actually changed (a real Partial<RolePatch>).
// =============================================================================

import { useState } from 'react';
import type { RolePatch, RoleRecord } from '../lib/api';
import { VoiceTextarea } from './VoiceTextarea';
import styles from './RoleEditForm.module.css';

export interface RoleEditFormProps {
  role: RoleRecord;
  saving: boolean;
  onSave: (patch: RolePatch) => Promise<string | null>;
  onCancel: () => void;
}

export function RoleEditForm({ role, saving, onSave, onCancel }: RoleEditFormProps) {
  const [company, setCompany] = useState(role.company);
  const [title, setTitle] = useState(role.title);
  const [location, setLocation] = useState(role.location ?? '');
  const [employerDescriptor, setEmployerDescriptor] = useState(role.employerDescriptor ?? '');
  const [startDate, setStartDate] = useState(role.startDate.slice(0, 10));
  const [endDate, setEndDate] = useState((role.endDate ?? '').slice(0, 10));
  const [isCurrent, setIsCurrent] = useState(role.isCurrent === 1);
  const [summary, setSummary] = useState(role.summary ?? '');
  const [error, setError] = useState<string | null>(null);

  const canSave = company.trim().length > 0 && title.trim().length > 0 && startDate.length > 0;

  async function handleSave() {
    if (!canSave || saving) return;
    setError(null);
    const err = await onSave({
      company: company.trim(),
      title: title.trim(),
      location: location.trim() || null,
      employerDescriptor: employerDescriptor.trim() || null,
      startDate,
      endDate: isCurrent ? null : endDate || null,
      isCurrent,
      summary: summary.trim() || null,
    });
    if (err) setError(err);
  }

  return (
    <div className={styles.form}>
      <div className={styles.row}>
        <VoiceTextarea label="Company" value={company} onChange={setCompany} rows={1} disabled={saving} />
        <VoiceTextarea label="Title" value={title} onChange={setTitle} rows={1} disabled={saving} />
      </div>

      <div className={styles.row}>
        <div className={styles.dateField}>
          <label className={styles.dateLabel}>Start date</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            disabled={saving}
            className={styles.dateInput}
          />
        </div>
        <div className={styles.dateField}>
          <label className={styles.dateLabel}>End date</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            disabled={saving || isCurrent}
            className={styles.dateInput}
          />
        </div>
      </div>

      <label className={styles.currentToggle}>
        <input
          type="checkbox"
          checked={isCurrent}
          onChange={(e) => setIsCurrent(e.target.checked)}
          disabled={saving}
        />
        Current role (clears end date)
      </label>

      <VoiceTextarea
        label="Location"
        value={location}
        onChange={setLocation}
        rows={1}
        hint="optional"
        disabled={saving}
      />

      <VoiceTextarea
        label="Employer descriptor"
        value={employerDescriptor}
        onChange={setEmployerDescriptor}
        rows={2}
        hint="optional — a line of context for a reader who may not know the company"
        disabled={saving}
      />

      <VoiceTextarea
        label="Summary"
        value={summary}
        onChange={setSummary}
        rows={2}
        hint="optional"
        disabled={saving}
      />

      {error ? (
        <div className={styles.errorRow} role="alert">
          {error}
        </div>
      ) : null}

      <div className={styles.actions}>
        <button type="button" className={styles.cancelButton} onClick={onCancel} disabled={saving}>
          Cancel
        </button>
        <button
          type="button"
          className={styles.saveButton}
          onClick={handleSave}
          disabled={!canSave || saving}
        >
          {saving ? <span className={styles.spinner} aria-hidden="true" /> : null}
          Save
        </button>
      </div>
    </div>
  );
}
